import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import IBaseState from '../../types/IBaseState';
import { AppState } from '../../reducers/root.reducer';
import Admin360QuestionnaireService from '../services/Admin360QuestionnaireService';
import ProjectQuestionnaire from '../types/project/ProjectQuestionnaire';
import { RequestServiceResponse } from '@tcc-online/request-service';
import ProjectTranslation from '../types/project/ProjectTranslation';
import Section from '../types/project/360/Section';
import Subsection from '../types/project/360/Subsection';
import Question from '../types/project/360/Question';
import IPaginationState from '../../types/state/IPaginationState';
import { createNotifiedThunk } from '../../reducers/createAppAsyncThunk';
import UploadState from '../../types/UploadState';
import QuestionTranslation from '../types/project/360/QuestionTranslation';
import { type AdminProjectType } from '../constants/AdminProjectTypes';

const NAME = 'Admin360Questionnaire';

const getQuestionnaires = createNotifiedThunk(
    `${NAME}/getQuestionnaires`,
    Admin360QuestionnaireService.getQuestionnaires,
    { error: 'fe.admin.error.load-questionnaires' },
);
const getQuestionnaire = createNotifiedThunk(
    `${NAME}/getQuestionnaire`,
    Admin360QuestionnaireService.getQuestionnaire,
    { error: 'fe.admin.error.load-questionnaire' },
);
const createQuestionnaire = createNotifiedThunk(
    `${NAME}/createQuestionnaire`,
    Admin360QuestionnaireService.createQuestionnaire,
    { error: 'fe.admin.error.create-questionnaire' },
);
const updateQuestionnaire = createNotifiedThunk(
    `${NAME}/updateQuestionnaire`,
    Admin360QuestionnaireService.updateQuestionnaire,
    { error: 'fe.admin.error.update-questionnaire' },
);
const deleteQuestionnaire = createNotifiedThunk(
    `${NAME}/deleteQuestionnaire`,
    Admin360QuestionnaireService.deleteQuestionnaire,
    { error: 'fe.admin.error.delete-questionnaire' },
);
const copyQuestionnaire = createNotifiedThunk(
    `${NAME}/copyQuestionnaire`,
    Admin360QuestionnaireService.copyQuestionnaire,
    { error: 'fe.admin.error.copy-questionnaire' },
);

const getSections = createNotifiedThunk(
    `${NAME}/getSections`,
    Admin360QuestionnaireService.getSections,
    { error: 'fe.admin.error.load-sections' },
);
const getSection = createNotifiedThunk(
    `${NAME}/getSection`,
    Admin360QuestionnaireService.getSection,
    { error: 'fe.admin.error.load-section' },
);
const createSection = createNotifiedThunk(
    `${NAME}/createSection`,
    Admin360QuestionnaireService.createSection,
    { error: 'fe.admin.error.create-section' },
);
const updateSection = createNotifiedThunk(
    `${NAME}/updateSection`,
    Admin360QuestionnaireService.updateSection,
    { error: 'fe.admin.error.update-section' },
);

const getSubsections = createNotifiedThunk(
    `${NAME}/getSubsections`,
    Admin360QuestionnaireService.getSubsections,
    { error: 'fe.admin.error.load-sections' },
);
const getSubsection = createNotifiedThunk(
    `${NAME}/getSubsection`,
    Admin360QuestionnaireService.getSubsection,
    { error: 'fe.admin.error.load-subsection' },
);
const createSubsection = createNotifiedThunk(
    `${NAME}/createSubsection`,
    Admin360QuestionnaireService.createSubsection,
    { error: 'fe.admin.error.create-subsection' },
);
const updateSubsection = createNotifiedThunk(
    `${NAME}/updateSubsection`,
    Admin360QuestionnaireService.updateSubsection,
    { error: 'fe.admin.error.update-subsection' },
);

const getQuestions = createNotifiedThunk(
    `${NAME}/getQuestions`,
    Admin360QuestionnaireService.getQuestions,
    { error: 'fe.admin.error.load-sections' },
);
const getQuestion = createNotifiedThunk(
    `${NAME}/getQuestion`,
    Admin360QuestionnaireService.getQuestion,
    { error: 'fe.admin.error.load-subsection' },
);
const createQuestion = createNotifiedThunk(
    `${NAME}/createQuestion`,
    Admin360QuestionnaireService.createQuestion,
    { error: 'fe.admin.error.create-question' },
);
const updateQuestion = createNotifiedThunk(
    `${NAME}/updateQuestion`,
    Admin360QuestionnaireService.updateQuestion,
    { error: 'fe.admin.error.update-questions' },
);
const deleteQuestion = createNotifiedThunk(
    `${NAME}/deleteQuestion`,
    Admin360QuestionnaireService.deleteQuestion,
    { error: 'fe.admin.error.delete-question' },
);

const getTranslations = createNotifiedThunk(
    `${NAME}/getTranslations`,
    Admin360QuestionnaireService.getTranslations,
    { error: 'fe.admin.error.load-translations' },
);
const saveTranslations = createNotifiedThunk(
    `${NAME}/saveTranslations`,
    Admin360QuestionnaireService.saveTranslations,
    { error: 'fe.admin.error.save-translations' },
);
const saveTranslation = createNotifiedThunk(
    `${NAME}/saveTranslation`,
    Admin360QuestionnaireService.saveTranslation,
    { error: 'fe.admin.error.save-translations' },
);
const addLanguage = createNotifiedThunk(
    `${NAME}/addLanguage`,
    Admin360QuestionnaireService.addLanguage,
    { error: 'fe.admin.error.save-language' },
);
const removeLanguage = createNotifiedThunk(
    `${NAME}/removeLanguage`,
    Admin360QuestionnaireService.removeLanguage,
    { error: 'fe.admin.error.remove-language' },
);

const getQuestionTranslations = createNotifiedThunk(
    `${NAME}/getQuestionTranslations`,
    Admin360QuestionnaireService.getQuestionTranslations,
    { error: 'fe.admin.error.load-question-translations' },
);
const updateQuestionTranslations = createNotifiedThunk(
    `${NAME}/updateQuestionTranslations`,
    Admin360QuestionnaireService.updateQuestionTranslations,
    { error: 'fe.admin.error.save-question-translations' },
);
const updateQuestionTranslation = createNotifiedThunk(
    `${NAME}/updateQuestionTranslation`,
    Admin360QuestionnaireService.updateQuestionTranslation,
    { error: 'fe.admin.error.save-question-translation' },
);

const autoTranslate = createNotifiedThunk(
    `${NAME}/autoTranslate`,
    Admin360QuestionnaireService.autoTranslate,
    { error: 'fe.admin.error.auto-translate' },
);
const autoTranslateQuestions = createNotifiedThunk(
    `${NAME}/autoTranslateQuestions`,
    Admin360QuestionnaireService.autoTranslateQuestions,
    { error: 'fe.admin.error.auto-translate' },
);

export interface Admin360QuestionnaireState {
    list: IPaginationState<ProjectQuestionnaire[]> &
        UploadState<ProjectQuestionnaire[]>;
    detail: IBaseState<ProjectQuestionnaire | null>;
    sections: IBaseState<Section[]>;
    translations: IBaseState<{ [key: string]: ProjectTranslation[] } | null>;
    questionTranslations: IBaseState<{
        [key: string]: QuestionTranslation[];
    } | null>;
}

const initialState: Admin360QuestionnaireState = {
    list: {
        data: [],
        loading: false,
        pagination: { page: 1 },
        uploading: false,
    },
    detail: {
        data: null,
        loading: false,
    },
    sections: {
        data: [],
        loading: false,
    },
    translations: {
        data: null,
        loading: false,
    },
    questionTranslations: {
        data: null,
        loading: false,
    },
};

const slice = createSlice({
    name: NAME,
    initialState,
    reducers: {},
    extraReducers: (builder): void => {
        builder.addCase(
            getQuestionnaires.pending,
            (state: Admin360QuestionnaireState): void => {
                state.list.loading = true;
                state.list.data = [];
            },
        );
        builder.addCase(
            getQuestionnaires.fulfilled,
            (
                state: Admin360QuestionnaireState,
                action: PayloadAction<
                    RequestServiceResponse<ProjectQuestionnaire[]>
                >,
            ): void => {
                state.list.data = action.payload.data;
                state.list.pagination = (action.payload as any).pagination;
            },
        );

        builder.addCase(
            getQuestionnaire.pending,
            (state: Admin360QuestionnaireState): void => {
                state.detail.loading = true;
                state.detail.data = null;
            },
        );
        builder.addCase(
            getQuestionnaire.fulfilled,
            (
                state: Admin360QuestionnaireState,
                action: PayloadAction<
                    RequestServiceResponse<ProjectQuestionnaire>
                >,
            ): void => {
                state.detail.data = action.payload.data;
            },
        );

        builder.addCase(
            getSections.pending,
            (state: Admin360QuestionnaireState): void => {
                state.sections.data = [];
            },
        );

        builder.addCase(
            getSections.fulfilled,
            (
                state: Admin360QuestionnaireState,
                action: PayloadAction<RequestServiceResponse<Section[]>>,
            ): void => {
                state.sections.data = action.payload.data;
            },
        );

        builder.addCase(
            getSubsections.fulfilled,
            (state: Admin360QuestionnaireState, action): void => {
                const sectionId: number = action.meta.arg.sectionId;
                if (!state.sections.data) {
                    return;
                }

                const index: number = findSectionIndex(
                    state.sections.data,
                    sectionId,
                );

                if (index < 0) {
                    return;
                }

                state.sections.data[index].subsections = action.payload.data;
            },
        );

        builder.addCase(
            getQuestions.fulfilled,
            (state: Admin360QuestionnaireState, action): void => {
                const { sectionId, subsectionId } = action.meta.arg;
                if (!state.sections.data) {
                    return;
                }

                const sectionIndex: number = findSectionIndex(
                    state.sections.data,
                    sectionId,
                );
                const subsectionIndex: number = findSubsectionIndex(
                    state.sections.data,
                    sectionIndex,
                    subsectionId,
                );
                if (subsectionIndex < 0) {
                    return;
                }

                state.sections.data[sectionIndex].subsections[
                    subsectionIndex
                ].questions = action.payload.data;
            },
        );

        builder.addCase(
            updateSection.fulfilled,
            (state: Admin360QuestionnaireState, action): void => {
                const sectionId: number = action.meta.arg.sectionId;
                const index: number = findSectionIndex(
                    state.sections.data,
                    sectionId,
                );
                if (index >= 0) {
                    state.sections.data[index] = action.payload.data;
                }
            },
        );

        builder.addCase(
            updateSubsection.fulfilled,
            (state: Admin360QuestionnaireState, action): void => {
                const { sectionId, subsectionId } = action.meta.arg;

                if (!state.sections.data) {
                    return;
                }

                const sectionIndex: number = findSectionIndex(
                    state.sections.data,
                    sectionId,
                );
                const subsectionIndex: number = findSubsectionIndex(
                    state.sections.data,
                    sectionIndex,
                    subsectionId,
                );

                if (subsectionIndex < 0) {
                    return;
                }

                state.sections.data[sectionIndex].subsections[subsectionIndex] =
                    action.payload.data;
            },
        );

        builder.addCase(
            updateQuestion.fulfilled,
            (state: Admin360QuestionnaireState, action): void => {
                const { sectionId, subsectionId, questionId } = action.meta.arg;

                const questions = findQuestionsInSections(
                    state.sections.data,
                    sectionId,
                    subsectionId,
                );
                if (!questions || !questions.length) {
                    return;
                }

                const questionIndex: number = findQuestionIndex(
                    questions,
                    questionId,
                );
                if (questionIndex < 0) {
                    return;
                }

                questions[questionIndex] = action.payload.data;
            },
        );

        builder.addCase(
            deleteQuestion.fulfilled,
            (state: Admin360QuestionnaireState, action): void => {
                const { sectionId, subsectionId, questionId } = action.meta.arg;

                const questions = findQuestionsInSections(
                    state.sections.data,
                    sectionId,
                    subsectionId,
                );
                if (!questions || !questions.length) {
                    return;
                }

                const questionIndex: number = findQuestionIndex(
                    questions,
                    questionId,
                );
                if (questionIndex < 0) {
                    return;
                }

                questions.splice(questionIndex, 1);
            },
        );

        builder.addCase(
            getTranslations.pending,
            (state: Admin360QuestionnaireState): void => {
                state.translations.loading = true;
                state.translations.data = null;
            },
        );
        builder.addCase(
            getTranslations.fulfilled,
            (
                state: Admin360QuestionnaireState,
                action: PayloadAction<
                    RequestServiceResponse<ProjectTranslation[]>
                > & {
                    meta: {
                        arg: {
                            productType: AdminProjectType;
                            questionnaireId: number;
                            locale: string;
                        };
                    };
                },
            ): void => {
                if (!state.translations.data) {
                    state.translations.data = {};
                }
                const locale = action.meta.arg.locale;
                state.translations.data[locale] = action.payload.data;
            },
        );
        builder.addCase(
            getQuestionTranslations.pending,
            (state: Admin360QuestionnaireState): void => {
                state.questionTranslations.loading = true;
            },
        );
        builder.addCase(
            getQuestionTranslations.fulfilled,
            (
                state: Admin360QuestionnaireState,
                action: PayloadAction<
                    RequestServiceResponse<QuestionTranslation[]>
                > & {
                    meta: {
                        arg: {
                            productType: AdminProjectType;
                            questionnaireId: number;
                            locale: string;
                        };
                    };
                },
            ): void => {
                if (!state.questionTranslations.data) {
                    state.questionTranslations.data = {};
                }
                const locale = action.meta.arg.locale;
                state.questionTranslations.data[locale] = action.payload.data;
            },
        );

        builder.addCase(
            addLanguage.fulfilled,
            (state: Admin360QuestionnaireState, action) => {
                if (state.detail.data) {
                    state.detail.data.languages = action.payload.data.languages;
                }
            },
        );
        builder.addCase(
            removeLanguage.fulfilled,
            (state: Admin360QuestionnaireState, action) => {
                if (state.detail.data) {
                    state.detail.data.languages = action.payload.data.languages;
                }
            },
        );

        builder.addCase(
            saveTranslation.fulfilled,
            (state: Admin360QuestionnaireState, action): void => {
                const { locale, translation } = action.meta.arg;
                const localeTranslations = state.translations.data?.[locale];
                if (!localeTranslations) return;
                const index = localeTranslations.findIndex(
                    (t) => t.id === translation.id,
                );
                if (index >= 0) {
                    localeTranslations[index] = translation;
                }
            },
        );

        builder.addCase(
            updateQuestionTranslation.fulfilled,
            (state: Admin360QuestionnaireState, action): void => {
                const { locale, translation } = action.meta.arg;
                const localeTranslations =
                    state.questionTranslations.data?.[locale];
                if (!localeTranslations) return;
                const index = localeTranslations.findIndex(
                    (t) => t.id === translation.id,
                );
                if (index >= 0) {
                    localeTranslations[index] = translation;
                }
            },
        );

        builder.addMatcher(
            isAnyOf(getQuestionnaires.fulfilled, getQuestionnaires.rejected),
            (state: Admin360QuestionnaireState): void => {
                state.list.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(getQuestionnaire.fulfilled, getQuestionnaire.rejected),
            (state: Admin360QuestionnaireState): void => {
                state.detail.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(
                getSections.pending,
                updateSection.pending,
                getSubsections.pending,
            ),
            (state: Admin360QuestionnaireState): void => {
                state.sections.loading = true;
            },
        );
        builder.addMatcher(
            isAnyOf(
                getSections.fulfilled,
                getSections.rejected,
                updateSection.fulfilled,
                updateSection.rejected,
                getSubsections.fulfilled,
                getSubsections.rejected,
            ),
            (state: Admin360QuestionnaireState): void => {
                state.sections.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(getTranslations.fulfilled, getTranslations.rejected),
            (state: Admin360QuestionnaireState): void => {
                state.translations.loading = false;
            },
        );
        builder.addMatcher(
            isAnyOf(
                getQuestionTranslations.fulfilled,
                getQuestionTranslations.rejected,
            ),
            (state: Admin360QuestionnaireState): void => {
                state.questionTranslations.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(
                createQuestionnaire.pending,
                updateQuestionnaire.pending,
                deleteQuestionnaire.pending,
                copyQuestionnaire.pending,
            ),
            (state: Admin360QuestionnaireState): void => {
                state.list.uploading = true;
            },
        );

        builder.addMatcher(
            isAnyOf(
                createQuestionnaire.fulfilled,
                updateQuestionnaire.fulfilled,
                deleteQuestionnaire.fulfilled,
                copyQuestionnaire.fulfilled,
                createQuestionnaire.rejected,
                updateQuestionnaire.rejected,
                deleteQuestionnaire.rejected,
                copyQuestionnaire.rejected,
            ),
            (state: Admin360QuestionnaireState): void => {
                state.list.uploading = false;
            },
        );
    },
});

const state = (state: AppState): Admin360QuestionnaireState =>
    state.admin['360'].questionnaire;

const Admin360QuestionnaireSlice = {
    reducer: slice.reducer,
    actions: {
        getQuestionnaires,
        getQuestionnaire,
        createQuestionnaire,
        updateQuestionnaire,
        deleteQuestionnaire,
        copyQuestionnaire,
        getSections,
        getSection,
        createSection,
        updateSection,
        getSubsections,
        getSubsection,
        createSubsection,
        updateSubsection,
        getQuestions,
        getQuestion,
        createQuestion,
        updateQuestion,
        getTranslations,
        deleteQuestion,
        saveTranslations,
        addLanguage,
        removeLanguage,
        getQuestionTranslations,
        updateQuestionTranslations,
        saveTranslation,
        updateQuestionTranslation,
        autoTranslate,
        autoTranslateQuestions,
    },
    state: {
        all: createSelector(
            state,
            (state: Admin360QuestionnaireState) => state,
        ),
        list: createSelector(
            state,
            (state: Admin360QuestionnaireState) => state.list,
        ),
        detail: createSelector(
            state,
            (state: Admin360QuestionnaireState) => state.detail,
        ),
        sections: createSelector(
            state,
            (state: Admin360QuestionnaireState) => state.sections,
        ),
        translations: createSelector(
            state,
            (state: Admin360QuestionnaireState) => state.translations,
        ),
        translationsByLocale: createSelector(
            [state, (state: AppState, locale: string) => locale],
            (state: Admin360QuestionnaireState, locale: string) =>
                state.translations?.data?.[locale],
        ),
        questionTranslations: createSelector(
            state,
            (state: Admin360QuestionnaireState) => state.questionTranslations,
        ),
        questionTranslationsByLocale: createSelector(
            [state, (state: AppState, locale: string) => locale],
            (state: Admin360QuestionnaireState, locale: string) =>
                state.questionTranslations?.data?.[locale],
        ),
    },
};

export default Admin360QuestionnaireSlice;

function findSectionIndex(sections: Section[], sectionId: number) {
    return sections.findIndex(
        (section: Section): boolean => section.id === sectionId,
    );
}

function findSubsectionIndex(
    sections: Section[],
    sectionIndex: number,
    subsectionId: number,
) {
    if (sectionIndex < 0 || !sections[sectionIndex]?.subsections) {
        return -1;
    }

    return sections[sectionIndex].subsections.findIndex(
        (subsection: Subsection): boolean => subsection.id === subsectionId,
    );
}

function findQuestionIndex(questions: Question[], questionId: number) {
    return questions.findIndex(
        (question: Question) => question.id === questionId,
    );
}

function findQuestionsInSections(
    sections: Section[],
    sectionId: number,
    subsectionId: number,
) {
    if (!sections) {
        return;
    }

    const sectionIndex: number = findSectionIndex(sections, sectionId);
    const subsectionIndex: number = findSubsectionIndex(
        sections,
        sectionIndex,
        subsectionId,
    );
    if (subsectionIndex < 0) {
        return;
    }

    const questions: Question[] =
        sections[sectionIndex].subsections[subsectionIndex].questions;
    if (!questions) {
        return;
    }

    return sections[sectionIndex].subsections[subsectionIndex].questions;
}
